import { createFeature, createReducer, on, createActionGroup, emptyProps } from '@ngrx/store';
import { zefActionPayload, zefActionSuccessPayload, zefActionFailPayload } from '@zerops/zef/core';
import { TokenData } from '@zerops/zef/auth';
import { WebAuthnLoginStartResponse } from '@zerops/models/auth';
import { FEATURE_NAME } from './sudo-reauth.constant';

// # Actions
export const sudoReauthActions = createActionGroup({
  source: FEATURE_NAME,
  events: {
    // User actions
    'submit Password': zefActionPayload<{ password: string }>(),
    'submit TOTP': zefActionPayload<{ token: string }>(),
    'start WebAuthn': emptyProps(),
    'cancel': emptyProps(),

    // Lifecycle actions
    'component Destroyed': emptyProps(),

    // API response actions - Password
    'submit Password Success': zefActionSuccessPayload<TokenData>(),
    'submit Password Failure': zefActionFailPayload,

    // API response actions - TOTP
    'submit TOTP Success': zefActionSuccessPayload<TokenData>(),
    'submit TOTP Failure': zefActionFailPayload,

    // API response actions - WebAuthn
    'start WebAuthn Success': zefActionSuccessPayload<WebAuthnLoginStartResponse>(),
    'start WebAuthn Failure': zefActionFailPayload,
    'finish WebAuthn': zefActionPayload<{ sessionId: string; response: any }>(),
    'finish WebAuthn Success': zefActionSuccessPayload<TokenData>(),
    'finish WebAuthn Failure': zefActionFailPayload
  }
});

// # State
/**
 * Sudo Re-authentication State
 *
 * Manages ephemeral state for sudo mode re-authentication flow.
 * This state is reset after successful authentication or cancellation.
 *
 * @property webAuthnSessionId - Active WebAuthn challenge session ID (for multi-step WebAuthn flow)
 */
export interface SudoReauthState {
  webAuthnSessionId: string | null;
}

const initialState: SudoReauthState = {
  webAuthnSessionId: null
};

export const sudoReauthFeature = createFeature({
  name: FEATURE_NAME,
  reducer: createReducer(
    initialState,

    /**
     * Store WebAuthn session ID when challenge is initiated
     * Used to complete the WebAuthn flow in finishWebAuthn action
     */
    on(sudoReauthActions.startWebAuthnSuccess, (state, { data }) => ({
      ...state,
      webAuthnSessionId: data.sessionId
    })),

    /**
     * Reset all state when authentication completes or user cancels
     */
    on(
      sudoReauthActions.submitPasswordSuccess,
      sudoReauthActions.submitTOTPSuccess,
      sudoReauthActions.finishWebAuthnSuccess,
      sudoReauthActions.cancel,
      () => initialState
    )
  )
});

export const {
  name: sudoReauthFeatureKey,
  reducer: sudoReauthReducer,
  selectWebAuthnSessionId
} = sudoReauthFeature;
